import React, { useEffect, useState } from "react";
import { Box, Button, Card, CardContent, CircularProgress, IconButton, TextField, Typography } from "@mui/material";
import { Add as AddIcon, Delete as DeleteIcon } from "@mui/icons-material";
import { usePermissions } from "../../hooks";
import { useNavigate } from "react-router";
import { useNotification } from "@refinedev/core";
import { apiVersion, apiJson } from '../../utils/api';

export const PlaceCreate: React.FC = () => {
  const { hasFullAccess } = usePermissions();
  const navigate = useNavigate();
  const { open: notify } = useNotification();
  const [urls, setUrls] = useState<string[]>([""]);
  const [bulkMode, setBulkMode] = useState(false);
  const [bulkText, setBulkText] = useState("");
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (!hasFullAccess) navigate("/places");
  }, [hasFullAccess, navigate]);

  const addUrlField = () => setUrls((prev) => [...prev, ""]);
  const updateUrl = (index: number, value: string) => setUrls((prev) => prev.map((u, i) => (i === index ? value : u)));
  const removeUrl = (index: number) => setUrls((prev) => prev.filter((_, i) => i !== index));

  const toggleMode = () => {
    if (!bulkMode) {
      setBulkText(urls.join(","));
    } else {
      setUrls(bulkText.split(",").map((s) => s.trim()).filter(Boolean));
    }
    setBulkMode((m) => !m);
  };

  const handleCreateFromUrls = () => {
    if (isLoading) return;
    const cleaned = bulkMode
      ? bulkText.split(",").map((s) => s.trim()).filter(Boolean)
      : urls.map((u) => u.trim()).filter(Boolean);

    if (cleaned.length === 0) {
      notify?.({
        type: "error",
        message: "Please add at least one Google Maps URL"
      });
      return;
    }
    setIsLoading(true);
    apiJson(`admin/${apiVersion}/places/import`, {
      method: "POST",
      body: JSON.stringify({ maps_url: cleaned })
    })
      .then(() => {
        notify?.({
          type: "success",
          message: "Places import started"
        });
        navigate("/places");
      })
      .catch((error) => {
        notify?.({
          type: "error",
          message: error instanceof Error ? error.message : "Failed to import places"
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  };

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "50vh" }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, maxWidth: 900, margin: "0 auto" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3 }}>
        <Typography variant="h4">Create New Place</Typography>
        <Button variant="contained" onClick={handleCreateFromUrls}>
          Create
        </Button>
      </Box>

      <Card>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2 }}>
            <Typography variant="h6">Google Maps Place URLs</Typography>
          </Box>
          {!bulkMode ? (
            <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
              {urls.map((url, index) => (
                <Box key={index} sx={{ display: "flex", gap: 1, alignItems: "center" }}>
                  <TextField
                    fullWidth
                    placeholder="https://maps.app.goo.gl/... or https://www.google.com/maps/place/..."
                    value={url}
                    onChange={(e) => updateUrl(index, e.target.value)}
                  />
                  {urls.length > 1 && (
                    <IconButton aria-label="remove" onClick={() => removeUrl(index)} color="error">
                      <DeleteIcon />
                    </IconButton>
                  )}
                </Box>
              ))}
              <Box>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={addUrlField}>
                  Add another
                </Button>
              </Box>
            </Box>
          ) : (
            <TextField
              fullWidth
              placeholder="Paste comma-separated Google Maps Place URLs"
              value={bulkText}
              onChange={(e) => setBulkText(e.target.value)}
            />
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default PlaceCreate;
